import React, { forwardRef } from 'react';
import { Material3ButtonProps, AccessibilityProps } from '../../types/material3';
import clsx from 'clsx';

interface EnhancedButtonProps extends Material3ButtonProps, AccessibilityProps {
  loading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  tooltip?: string;
  role?: string;
  'aria-expanded'?: boolean;
  'aria-pressed'?: boolean;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-controls'?: string;
  'aria-live'?: 'polite' | 'assertive' | 'off';
}

export const Material3Button = forwardRef<HTMLButtonElement, EnhancedButtonProps>(
  (
    {
      variant = 'filled',
      size = 'md',
      icon,
      leftIcon,
      rightIcon,
      children,
      onClick,
      disabled = false,
      loading = false,
      loadingText = 'Loading...',
      fullWidth = false,
      className,
      role,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-expanded': ariaExpanded,
      'aria-pressed': ariaPressed,
      'aria-haspopup': ariaHaspopup,
      'aria-controls': ariaControls,
      'aria-live': ariaLive,
      ...rest
    },
    ref
  ) => {
    const triggerHaptic = () => {
      if (typeof window === 'undefined' || !('vibrate' in navigator)) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      navigator.vibrate(8);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || loading) return;
      onClick?.(event);
    };

    const handlePointerDown = () => {
      if (!disabled && !loading) triggerHaptic();
    };

    const getVariantStyles = () => {
      const base = 'relative rounded-full font-semibold transition-colors duration-150 ease-out';
      switch (variant) {
        case 'outlined':
          return clsx(base, 'border border-primary-500 bg-white text-primary-600 hover:bg-primary-50 active:bg-primary-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500');
        case 'elevated':
          return clsx(base, 'border border-outline bg-white text-primary-600 shadow-sm hover:bg-surface-200 active:bg-surface-variant focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500');
        case 'text':
          return clsx(base, 'bg-transparent text-primary-600 hover:bg-primary-50 active:bg-primary-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500');
        case 'filled':
        default:
          return clsx(base, 'bg-primary-500 text-white shadow-sm hover:bg-primary-600 active:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500');
      }
    };

    const getSizeStyles = () => {
      switch (size) {
        case 'sm':
          return 'min-h-[38px] gap-1 px-4 py-2 text-xs';
        case 'lg':
          return 'min-h-[48px] gap-2 px-7 py-3 text-base';
        case 'md':
        default:
          return 'min-h-[44px] gap-2 px-6 py-2.5 text-sm';
      }
    };

    const renderIcon = (iconElement: React.ReactNode, position: 'left' | 'right' | 'only' = 'only') => {
      if (!iconElement) return null;
      return <span className={clsx('flex items-center justify-center', position === 'only' ? 'h-5 w-5' : 'h-4 w-4')}>{iconElement}</span>;
    };

    return (
      <div className={clsx(fullWidth && 'w-full', className)}>
        <button
          ref={ref}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          disabled={disabled || loading}
          role={role}
          aria-label={ariaLabel || (loading ? loadingText : undefined)}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-expanded={ariaExpanded}
          aria-pressed={ariaPressed}
          aria-haspopup={ariaHaspopup}
          aria-controls={ariaControls}
          aria-live={ariaLive}
          aria-busy={loading}
          aria-disabled={disabled || loading}
          tabIndex={disabled ? -1 : 0}
          className={clsx(
            'inline-flex min-w-[44px] items-center justify-center border-none font-sans leading-tight tracking-normal',
            'touch-manipulation select-none outline-none active:translate-y-px disabled:pointer-events-none disabled:opacity-50',
            getVariantStyles(),
            getSizeStyles(),
            fullWidth && 'w-full'
          )}
          {...rest}
        >
          {leftIcon && !loading && renderIcon(leftIcon, 'left')}
          {loading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
          {icon && !children && !loading && renderIcon(icon, 'only')}
          {children && <span className={clsx('flex items-center', (leftIcon || rightIcon || loading) && 'mx-1')}>{loading ? loadingText : children}</span>}
          {rightIcon && !loading && renderIcon(rightIcon, 'right')}
        </button>
      </div>
    );
  }
);

Material3Button.displayName = 'Material3Button';

export const Material3FAB = forwardRef<HTMLButtonElement, Omit<EnhancedButtonProps, 'variant' | 'size'> & { size?: 'sm' | 'md' | 'lg'; extended?: boolean }>(
  ({ size = 'md', extended = false, icon, children, className, ...props }, ref) => {
    const getFABStyles = () => {
      if (extended) return 'rounded-2xl';
      switch (size) {
        case 'sm': return 'h-10 w-10 p-0';
        case 'lg': return 'h-14 w-14 p-0';
        case 'md':
        default: return 'h-12 w-12 p-0';
      }
    };

    return (
      <Material3Button
        ref={ref}
        variant="filled"
        className={clsx(getFABStyles(), className)}
        icon={!extended ? icon : undefined}
        leftIcon={extended ? icon : undefined}
        {...props}
      >
        {extended ? children : undefined}
      </Material3Button>
    );
  }
);

Material3FAB.displayName = 'Material3FAB';

export const Material3IconButton = forwardRef<HTMLButtonElement, Omit<EnhancedButtonProps, 'children' | 'leftIcon' | 'rightIcon'> & { toggle?: boolean; toggled?: boolean; onToggle?: (toggled: boolean) => void }>(
  ({ icon, variant = 'text', size = 'md', toggle = false, toggled = false, onToggle, onClick, className, ...props }, ref) => {
    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
      if (toggle && onToggle) onToggle(!toggled);
      onClick?.(event);
    };

    return (
      <Material3Button
        ref={ref}
        variant={variant}
        size={size}
        icon={icon}
        onClick={handleClick}
        className={clsx('aspect-square rounded-full p-2', toggle && toggled && '!bg-primary-100 !text-primary-700', className)}
        {...props}
      />
    );
  }
);

Material3IconButton.displayName = 'Material3IconButton';

export { type EnhancedButtonProps };
